import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * A stylized true-X racing quad, built from primitives (Free's reference
 * photos: carbon X frame, four motors, boxy stack, FPV camera in the nose,
 * LED accents). Faces +z (toward the viewer).
 *
 * Animation is driven through a mutable `drive` object (off-React rule):
 *   hover     -> props spool up, the quad lifts and tilts to the 45-degree
 *                launch angle, the lens wakes and looks at you
 *   launching -> it punches out along its nose vector (the lens-wipe overlay
 *                covers the screen while it flies at the camera)
 * `glow.k` is the shared menu-illumination factor (0 dark deck, 1 lit).
 */

export type QuadDrive = { hover: boolean; launching: boolean };

const MOTORS: [number, number][] = [
  [0.34, 0.34],
  [-0.34, 0.34],
  [0.34, -0.34],
  [-0.34, -0.34],
];
const PROP_DIR = [1, -1, -1, 1];
const TILT = Math.PI / 4; // the launch-stand hover tilt: 45 degrees, per spec

export function DroneQuad({
  position,
  accent,
  drive,
  glow,
  scale = 1.15,
}: {
  position: [number, number, number];
  accent: string;
  drive: QuadDrive;
  glow: { k: number };
  scale?: number;
}) {
  const rootRef = useRef<THREE.Group>(null);
  const propRefs = useRef<(THREE.Group | null)[]>([]);
  const spool = useRef(0);
  const launchOff = useRef(new THREE.Vector3());

  const mats = useMemo(() => {
    const carbon = new THREE.MeshStandardMaterial({ color: "#171b22", roughness: 0.55, metalness: 0.3 });
    const dark = new THREE.MeshStandardMaterial({ color: "#0b0d13", roughness: 0.5, metalness: 0.2 });
    const motor = new THREE.MeshStandardMaterial({ color: accent, roughness: 0.32, metalness: 0.8 });
    const prop = new THREE.MeshStandardMaterial({ color: "#232833", roughness: 0.6, metalness: 0.1 });
    const disc = new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0, depthWrite: false });
    const lens = new THREE.MeshStandardMaterial({
      color: "#04050a",
      roughness: 0.15,
      metalness: 0.4,
      emissive: accent,
      emissiveIntensity: 0,
    });
    const led = new THREE.MeshStandardMaterial({
      color: "#101319",
      emissive: accent,
      emissiveIntensity: 0.05,
    });
    return { carbon, dark, motor, prop, disc, lens, led };
  }, [accent]);

  useFrame((_, dt) => {
    const root = rootRef.current;
    if (!root) return;

    const target = drive.hover || drive.launching ? 1 : 0;
    spool.current += (target - spool.current) * Math.min(1, dt * 4.5);
    const s = spool.current;

    // props: idle tick-over, rip when spooled (strobe + blur disc)
    const rpm = 2.5 + s * 85;
    for (let i = 0; i < 4; i++) {
      const p = propRefs.current[i];
      if (p) p.rotation.y += rpm * dt * PROP_DIR[i];
    }
    mats.disc.opacity = s * 0.3;

    // lift off the stand, tilt the nose up toward the camera
    root.rotation.x = -TILT * s;

    if (drive.launching) {
      // nose vector after the tilt: straight at the viewer, climbing
      const a = TILT * s;
      const speed = 6 + launchOff.current.length() * 7;
      launchOff.current.y += Math.sin(a) * speed * dt;
      launchOff.current.z += Math.cos(a) * speed * dt;
    } else if (launchOff.current.lengthSq() > 0) {
      launchOff.current.set(0, 0, 0); // back on the stand (workshop card closed)
    }
    root.position.set(
      position[0] + launchOff.current.x,
      position[1] + s * 0.22 + launchOff.current.y,
      position[2] + launchOff.current.z
    );

    // the lens wakes with the spool; LEDs ride the menu illumination
    mats.lens.emissiveIntensity = glow.k * (0.3 + s * 2.6);
    mats.led.emissiveIntensity = 0.05 + glow.k * (0.55 + s * 1.6);
    mats.motor.emissiveIntensity = 0;
  });

  return (
    <group ref={rootRef} position={position} scale={scale}>
      {/* arms: two crossed carbon slabs */}
      <mesh material={mats.carbon} rotation={[0, Math.PI / 4, 0]}>
        <boxGeometry args={[1.06, 0.028, 0.075]} />
      </mesh>
      <mesh material={mats.carbon} rotation={[0, -Math.PI / 4, 0]}>
        <boxGeometry args={[1.06, 0.028, 0.075]} />
      </mesh>

      {/* stack: bottom plate, canopy, FPV camera housing + lens */}
      <mesh material={mats.carbon} position={[0, 0.05, 0]}>
        <boxGeometry args={[0.3, 0.055, 0.46]} />
      </mesh>
      <mesh material={mats.dark} position={[0, 0.11, -0.02]}>
        <boxGeometry args={[0.2, 0.075, 0.3]} />
      </mesh>
      <mesh material={mats.dark} position={[0, 0.09, 0.21]}>
        <boxGeometry args={[0.13, 0.11, 0.07]} />
      </mesh>
      <mesh material={mats.lens} position={[0, 0.09, 0.255]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.042, 0.042, 0.03, 20]} />
      </mesh>

      {/* LED strip on the spine + a whip antenna out the back */}
      <mesh material={mats.led} position={[0, 0.15, -0.05]}>
        <boxGeometry args={[0.07, 0.014, 0.26]} />
      </mesh>
      <mesh material={mats.dark} position={[0, 0.17, -0.2]} rotation={[-0.55, 0, 0]}>
        <cylinderGeometry args={[0.011, 0.011, 0.24, 6]} />
      </mesh>

      {/* motors, props, blur discs */}
      {MOTORS.map(([mx, mz], i) => (
        <group key={i}>
          <mesh material={mats.motor} position={[mx, 0.035, mz]}>
            <cylinderGeometry args={[0.05, 0.058, 0.055, 12]} />
          </mesh>
          <group ref={(el) => { propRefs.current[i] = el; }} position={[mx, 0.078, mz]}>
            <mesh material={mats.prop}>
              <boxGeometry args={[0.3, 0.006, 0.034]} />
            </mesh>
          </group>
          <mesh material={mats.disc} position={[mx, 0.078, mz]}>
            <cylinderGeometry args={[0.152, 0.152, 0.004, 24]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}
